import { useCallback, useState } from "react";
import { extractFieldErrors } from "../data/validation";
import { mutationErrorMessage } from "./mutation-error-message";

export interface FieldErrorWrite {
  /** The refusal's field errors, by the request body's field name. */
  fieldErrors: Record<string, string>;
  /** The summary to show above the form: how many things to fix, or the refusal's own sentence. */
  banner: string | null;
  isBusy: boolean;
  /** Run a write; true when it went through. Clears the previous errors first. */
  run: (write: () => Promise<void>) => Promise<boolean>;
  reset: () => void;
}

/**
 * One form's write and its refusal, in the api-validation rule's shape: field errors to bind next to their inputs,
 * and a summary banner (the count, so a screen reader hears it at submission) or, for a refusal that is not about
 * a field (a 409), its sentence.
 */
export function useFieldErrorWrite(fallback: string): FieldErrorWrite {
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const reset = useCallback(() => {
    setFieldErrors({});
    setBanner(null);
  }, []);

  const run = useCallback(
    async (write: () => Promise<void>) => {
      reset();
      setIsBusy(true);
      try {
        await write();
        return true;
      } catch (err) {
        const errors = extractFieldErrors(err);
        if (errors.length > 0) {
          const byField: Record<string, string> = {};
          for (const e of errors) byField[e.field] ??= e.message;
          setFieldErrors(byField);
          setBanner(
            errors.length === 1
              ? "Please fix 1 thing below."
              : `Please fix ${errors.length} things below.`,
          );
        } else {
          setBanner(mutationErrorMessage(err, fallback));
        }
        return false;
      } finally {
        setIsBusy(false);
      }
    },
    [fallback, reset],
  );

  return { fieldErrors, banner, isBusy, run, reset };
}
